import { Download, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useCurrentMessage } from '@/contexts/CurrentMessageContext';
import { downloadSTLFile, downloadOpenSCADFile, downloadDXFFile } from '@/utils/downloadUtils';
import { useBlob } from '@/contexts/BlobContext';
import { useToast } from '@/hooks/use-toast';
import { useUnit } from '@/contexts/UnitContext';

type ExportFormat = 'stl' | 'scad' | 'dxf';

export function ExportSection() {
  const { blob } = useBlob();
  const { currentMessage } = useCurrentMessage();
  const { toast } = useToast();
  const { unitSystem, setUnitSystem } = useUnit();
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('stl');
  const [isExportingDXF, setIsExportingDXF] = useState(false);

  const handleDownload = async () => {
    if (selectedFormat === 'stl') {
      handleDownloadSTL();
    } else if (selectedFormat === 'scad') {
      handleDownloadOpenSCAD();
    } else if (selectedFormat === 'dxf') {
      await handleDownloadDXF();
    }
  };

  const handleDownloadSTL = () => {
    if (!blob) return;
    downloadSTLFile(blob, currentMessage);
  };

  const handleDownloadOpenSCAD = () => {
    if (!currentMessage?.content.artifact?.code) return;
    downloadOpenSCADFile(currentMessage.content.artifact.code, currentMessage);
  };

  const handleDownloadDXF = async () => {
    if (!currentMessage?.content.artifact?.code) return;

    setIsExportingDXF(true);

    try {
      const originalCode = currentMessage.content.artifact.code;
      const params = currentMessage.content.artifact.parameters || [];

      // Generate parameter definitions at the top level so they can be overridden
      // by OpenSCAD's -D flags
      const paramDefinitions = params.map(p => {
        let value = p.value;
        if (p.type === 'string') {
          value = `"${value}"`;
        } else if (p.type === 'boolean') {
          value = value ? 'true' : 'false';
        } else if (Array.isArray(value)) {
          value = `[${value.join(', ')}]`;
        }
        return `${p.name} = ${value};`;
      }).join('\n');

      // Remove any existing parameter definitions from original code
      // to avoid conflicts (they'll be defined at top level)
      let cleanedCode = originalCode;
      for (const p of params) {
        // Match lines like "paramName = value;" at the start of lines
        const regex = new RegExp(`^\\s*${p.name}\\s*=\\s*[^;]+;\\s*(?://.*)?$`, 'gm');
        cleanedCode = cleanedCode.replace(regex, `// ${p.name} defined at top level`);
      }

      // Create projection code with parameters at top level
      const projectionCode = `// DXF Export - 2D Flat Pattern (Top-Down Projection)
// Parameters defined at top level for override support
${paramDefinitions}

// Original geometry wrapped in module for projection
module _dxf_geometry() {
${cleanedCode}
}

// Project the 3D geometry to 2D (top-down view)
projection(cut = false) {
    _dxf_geometry();
}`;

      // Use the worker to generate DXF
      const worker = new Worker(
        new URL('@/worker/worker.ts', import.meta.url),
        { type: 'module' }
      );

      const result = await new Promise<Blob>((resolve, reject) => {
        worker.onmessage = async (event) => {
          const response = event.data;
          if (response.type === 'export') {
            if (response.err) {
              // Handle OpenSCAD errors
              const errorMessage = response.err.stdErr
                ? response.err.stdErr.join('\n')
                : response.err.message || 'Unknown error';
              reject(new Error(errorMessage));
            } else if (response.data?.output) {
              // Get the raw DXF content from OpenSCAD
              let dxfContent: string;
              if (response.data.output instanceof ArrayBuffer) {
                dxfContent = new TextDecoder().decode(response.data.output);
              } else if (response.data.output instanceof Uint8Array) {
                dxfContent = new TextDecoder().decode(response.data.output);
              } else {
                dxfContent = String(response.data.output);
              }

              // Convert OpenSCAD's LWPOLYLINE to simple LINE entities for R12 compatibility
              // OpenSCAD uses LWPOLYLINE with subclass markers (100/AcDbEntity) but no entity handles
              // This is incompatible with modern AutoCAD - it crashes. Convert to simple LINEs.

              // 1. Parse all LWPOLYLINE entities and extract vertices
              const lwpolylines: { points: { x: number, y: number }[], closed: boolean }[] = [];
              const lwpolyRegex = /0\nLWPOLYLINE[\s\S]*?(?=0\n(?:LWPOLYLINE|ENDSEC|LINE|ARC|CIRCLE))/g;
              let match;
              while ((match = lwpolyRegex.exec(dxfContent)) !== null) {
                const polyData = match[0];
                const points: { x: number, y: number }[] = [];

                // Check if closed (group code 70 with value 1)
                const closedMatch = polyData.match(/\s70\n\s*(\d+)/);
                const closed = closedMatch && (parseInt(closedMatch[1]) & 1) === 1;

                // Extract all vertices (group codes 10 and 20)
                const coordRegex = /\s10\n([\d.-]+)\n\s*20\n([\d.-]+)/g;
                let coordMatch;
                while ((coordMatch = coordRegex.exec(polyData)) !== null) {
                  points.push({ x: parseFloat(coordMatch[1]), y: parseFloat(coordMatch[2]) });
                }

                if (points.length > 0) {
                  lwpolylines.push({ points, closed: !!closed });
                }
              }

              // 2. Apply unit conversion - OpenSCAD always works in mm
              // For imperial, we need to divide by 25.4 to convert to inches
              const isImperial = unitSystem === 'imperial';
              const unitScale = isImperial ? 1 / 25.4 : 1;

              // Scale all points
              for (const poly of lwpolylines) {
                for (const point of poly.points) {
                  point.x *= unitScale;
                  point.y *= unitScale;
                }
              }

              // 3. Generate LINE entities from the polylines
              let lineEntities = '';
              for (const poly of lwpolylines) {
                for (let i = 0; i < poly.points.length - 1; i++) {
                  lineEntities += `  0
LINE
  8
0
 10
${poly.points[i].x.toFixed(6)}
 20
${poly.points[i].y.toFixed(6)}
 30
0.0
 11
${poly.points[i + 1].x.toFixed(6)}
 21
${poly.points[i + 1].y.toFixed(6)}
 31
0.0
`;
                }
                // Close the polyline if needed
                if (poly.closed && poly.points.length > 1) {
                  const last = poly.points[poly.points.length - 1];
                  const first = poly.points[0];
                  lineEntities += `  0
LINE
  8
0
 10
${last.x.toFixed(6)}
 20
${last.y.toFixed(6)}
 30
0.0
 11
${first.x.toFixed(6)}
 21
${first.y.toFixed(6)}
 31
0.0
`;
                }
              }

              // 4. Extract extents from original DXF and apply unit conversion
              const extMinMatch = dxfContent.match(/\$EXTMIN[\s\S]*?10\n\s*([\d.-]+)\n\s*20\n\s*([\d.-]+)/);
              const extMaxMatch = dxfContent.match(/\$EXTMAX[\s\S]*?10\n\s*([\d.-]+)\n\s*20\n\s*([\d.-]+)/);
              const minX = (extMinMatch ? parseFloat(extMinMatch[1]) : -50) * unitScale;
              const minY = (extMinMatch ? parseFloat(extMinMatch[2]) : -50) * unitScale;
              const maxX = (extMaxMatch ? parseFloat(extMaxMatch[1]) : 50) * unitScale;
              const maxY = (extMaxMatch ? parseFloat(extMaxMatch[2]) : 50) * unitScale;

              // Calculate view center and size for auto-zoom
              const centerX = (minX + maxX) / 2;
              const centerY = (minY + maxY) / 2;
              const viewWidth = maxX - minX;
              const viewHeight = maxY - minY;
              const viewSize = Math.max(viewWidth, viewHeight) * 1.1; // 10% padding

              // 4. Build a clean R12 DXF file with LINE entities and proper view settings
              // Using LUNITS=2 for decimal inches (AutoCAD default)
              const cleanDxf = `  0
SECTION
  2
HEADER
  9
$ACADVER
  1
AC1009
  9
$INSBASE
 10
0.0
 20
0.0
 30
0.0
  9
$EXTMIN
 10
${minX.toFixed(6)}
 20
${minY.toFixed(6)}
 30
0.0
  9
$EXTMAX
 10
${maxX.toFixed(6)}
 20
${maxY.toFixed(6)}
 30
0.0
  9
$LIMMIN
 10
${minX.toFixed(6)}
 20
${minY.toFixed(6)}
  9
$LIMMAX
 10
${maxX.toFixed(6)}
 20
${maxY.toFixed(6)}
  9
$VIEWCTR
 10
${centerX.toFixed(6)}
 20
${centerY.toFixed(6)}
  9
$VIEWSIZE
 40
${viewSize.toFixed(6)}
  9
$LUNITS
 70
${unitSystem === 'imperial' ? 2 : 4}
  9
$LUPREC
 70
4
  9
$MEASUREMENT
 70
${unitSystem === 'imperial' ? 0 : 1}
  0
ENDSEC
  0
SECTION
  2
TABLES
  0
TABLE
  2
VPORT
 70
1
  0
VPORT
  2
*ACTIVE
 70
0
 10
0.0
 20
0.0
 11
1.0
 21
1.0
 12
${centerX.toFixed(6)}
 22
${centerY.toFixed(6)}
 13
0.0
 23
0.0
 14
1.0
 24
1.0
 15
0.0
 25
0.0
 16
0.0
 26
0.0
 36
1.0
 17
0.0
 27
0.0
 37
0.0
 40
${viewSize.toFixed(6)}
 41
1.0
 42
50.0
 43
0.0
 44
0.0
 50
0.0
 51
0.0
 71
0
 72
100
 73
1
 74
1
 75
0
 76
0
 77
0
 78
0
  0
ENDTAB
  0
TABLE
  2
LTYPE
 70
1
  0
LTYPE
  2
CONTINUOUS
 70
0
  3
Solid line
 72
65
 73
0
 40
0.0
  0
ENDTAB
  0
TABLE
  2
LAYER
 70
1
  0
LAYER
  2
0
 70
0
 62
7
  6
CONTINUOUS
  0
ENDTAB
  0
ENDSEC
  0
SECTION
  2
BLOCKS
  0
ENDSEC
  0
SECTION
  2
ENTITIES
${lineEntities}  0
ENDSEC
  0
EOF
`;

              const blob = new Blob([cleanDxf], { type: 'application/dxf' });
              resolve(blob);
            } else {
              reject(new Error('No DXF output generated - the model may not have a valid 2D projection'));
            }
            worker.terminate();
          }
        };

        worker.onerror = (error) => {
          reject(new Error('Worker error: ' + error.message));
          worker.terminate();
        };

        // Generate unique ID for this request
        const requestId = 'dxf-export-' + Date.now();

        worker.postMessage({
          id: requestId,
          type: 'export',
          data: {
            code: projectionCode,
            fileType: 'dxf',
            params: currentMessage?.content.artifact?.parameters || [],
          }
        });
      });

      downloadDXFFile(result, currentMessage);

      toast({
        title: 'DXF Export Complete',
        description: '2D flat pattern exported successfully for laser/waterjet cutting.',
      });
    } catch (error) {
      console.error('DXF export error:', error);
      toast({
        title: 'DXF Export Failed',
        description: error instanceof Error ? error.message : 'Failed to generate 2D flat pattern. Make sure your model has a valid 2D projection.',
        variant: 'destructive',
      });
    } finally {
      setIsExportingDXF(false);
    }
  };

  const isDownloadDisabled =
    isExportingDXF ||
    (selectedFormat === 'stl' ? !blob : !currentMessage?.content.artifact?.code);

  return (
    <div className="flex h-full flex-col bg-dc-bg-secondary">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-dc-border-secondary bg-dc-bg-tertiary/50 px-4 py-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-dc-accent-primary/20 to-dc-accent-secondary/20">
          <Download className="h-4 w-4 text-dc-accent-primary" />
        </div>
        <span className="text-base font-semibold text-dc-text-primary">
          Export Model
        </span>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-6 px-6 py-6">
        {/* Format Selection */}
        <div className="flex flex-col gap-3">
          <span className="text-sm font-medium text-dc-text-secondary">Export Format</span>
          <div className="flex flex-col gap-2">
            {/* 3D Formats Row */}
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedFormat('stl')}
                disabled={!blob}
                className={`flex-1 rounded-xl border px-4 py-3 text-sm font-medium transition-all ${selectedFormat === 'stl'
                  ? 'border-dc-accent-primary bg-dc-accent-primary/20 text-dc-accent-primary'
                  : 'border-dc-border-secondary bg-dc-bg-tertiary text-dc-text-secondary hover:border-dc-border-primary'
                  } disabled:opacity-50`}
              >
                <div className="text-center">
                  <div className="font-semibold">.STL</div>
                  <div className="text-xs opacity-70">3D Printing</div>
                </div>
              </button>
              <button
                onClick={() => setSelectedFormat('scad')}
                disabled={!currentMessage?.content.artifact?.code}
                className={`flex-1 rounded-xl border px-4 py-3 text-sm font-medium transition-all ${selectedFormat === 'scad'
                  ? 'border-dc-accent-secondary bg-dc-accent-secondary/20 text-dc-accent-secondary'
                  : 'border-dc-border-secondary bg-dc-bg-tertiary text-dc-text-secondary hover:border-dc-border-primary'
                  } disabled:opacity-50`}
              >
                <div className="text-center">
                  <div className="font-semibold">.SCAD</div>
                  <div className="text-xs opacity-70">OpenSCAD Code</div>
                </div>
              </button>
            </div>

            {/* 2D Format Row */}
            <button
              onClick={() => setSelectedFormat('dxf')}
              disabled={!currentMessage?.content.artifact?.code}
              className={`w-full rounded-xl border px-4 py-3 text-sm font-medium transition-all ${selectedFormat === 'dxf'
                ? 'border-amber-500 bg-amber-500/20 text-amber-400'
                : 'border-dc-border-secondary bg-dc-bg-tertiary text-dc-text-secondary hover:border-dc-border-primary'
                } disabled:opacity-50`}
            >
              <div className="text-center">
                <div className="font-semibold">.DXF</div>
                <div className="text-xs opacity-70">2D Flat Pattern • Laser / Waterjet / Plasma</div>
              </div>
            </button>
          </div>
        </div>

        {/* DXF Info and Unit Toggle */}
        {selectedFormat === 'dxf' && (
          <div className="flex flex-col gap-3">
            {/* Unit Toggle */}
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-dc-text-secondary">Units</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setUnitSystem('imperial')}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-all ${unitSystem === 'imperial'
                    ? 'border-amber-500 bg-amber-500/20 text-amber-400'
                    : 'border-dc-border-secondary bg-dc-bg-tertiary text-dc-text-secondary hover:border-dc-border-primary'
                    }`}
                >
                  Inches
                </button>
                <button
                  onClick={() => setUnitSystem('metric')}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-all ${unitSystem === 'metric'
                    ? 'border-amber-500 bg-amber-500/20 text-amber-400'
                    : 'border-dc-border-secondary bg-dc-bg-tertiary text-dc-text-secondary hover:border-dc-border-primary'
                    }`}
                >
                  Millimeters
                </button>
              </div>
            </div>

            {/* Info Card */}
            <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-sm text-amber-300">
              <p className="font-medium mb-1">2D Flat Pattern Export</p>
              <p className="text-xs opacity-80">
                Creates a top-down projection of your 3D model for CNC cutting machines.
              </p>
            </div>
          </div>
        )}

        {/* Download Button */}
        <Button
          onClick={handleDownload}
          disabled={isDownloadDisabled}
          className="h-12 w-full rounded-xl bg-gradient-to-r from-dc-accent-primary to-dc-accent-secondary text-dc-bg-primary font-medium shadow-[0_0_15px_rgba(0,212,255,0.3)] transition-all hover:shadow-[0_0_25px_rgba(0,212,255,0.4)] disabled:opacity-50 disabled:shadow-none"
        >
          {isExportingDXF ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Generating 2D Pattern...
            </>
          ) : (
            <>
              <Download className="mr-2 h-5 w-5" />
              Download {selectedFormat.toUpperCase()}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
